import { Body, Icon } from '@assembly-js/design-system';
import { Menu, MenuItem } from '@mui/material';
import { t } from 'i18next';
import React, { useState } from 'react';

import { AdditionalCreditOption } from './studioPlanFeatures';

interface AdditionalCreditsSelectProps {
  options: AdditionalCreditOption[];
  selectedTotal: number;
  onSelectTotal: (totalCredits: number) => void;
  /**
   * When set, non-zero options read "+{n} additional {Plan} credits" (the
   * credits-on-current-plan phrasing); otherwise "{n} additional credits".
   */
  planTitle?: string;
}

/** The additional-credits dropdown shared by the upgrade/credits modals. */
export const AdditionalCreditsSelect: React.FC<
  AdditionalCreditsSelectProps
> = ({ options, selectedTotal, onSelectTotal, planTitle }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const selected =
    options.find((option) => option.totalCredits === selectedTotal) ??
    options.at(0);

  const optionLabel = (option: AdditionalCreditOption): string => {
    if (option.additionalCredits === 0) {
      return t(($) => $.page_studio_plans.plan_change.no_additional_credits);
    }
    if (planTitle !== undefined) {
      return t(
        ($) => $.page_studio_plans.plan_change.additional_plan_credits_option,
        { count: option.additionalCredits, plan: planTitle },
      );
    }
    return t(($) => $.page_studio_plans.plan_change.additional_credits_option, {
      count: option.additionalCredits,
    });
  };

  return (
    <>
      <button
        type="button"
        className="flex h-8 w-full cursor-pointer items-center justify-between rounded border border-solid border-divider-card bg-white px-2"
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <Body size="sm" tag="span" className="text-black-heading">
          {selected ? optionLabel(selected) : ''}
        </Body>
        <Icon icon="ChevronDown" className="h-4 w-4 text-black-heading" />
      </button>
      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{ className: 'min-w-[220px]' }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.totalCredits}
            selected={option.totalCredits === selectedTotal}
            onClick={() => {
              onSelectTotal(option.totalCredits);
              setAnchorEl(null);
            }}
          >
            <Body size="sm" tag="span">
              {optionLabel(option)}
            </Body>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
